import React from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { loadUrgentClientOnboarding, type ClientOnboardingShowAlert } from '../../../api/clientOnboardingAlerts';

/** Independent of the date/search filters: urgent onboarding flags must remain visible in every dashboard view. */
export default function ClientOnboardingDashboardNotice() {
  const context = useSelector((state: any) => `${state.auth?.userToken ?? ''}:${state.auth?.activeRole ?? ''}`);
  const [state, setState] = React.useState<{ context: string; shows: ClientOnboardingShowAlert[]; truncated?: boolean; error?: boolean }>({ context: '', shows: [] });
  const [revision, refresh] = React.useReducer(n => n + 1, 0);
  React.useEffect(() => {
    let current = true;
    void loadUrgentClientOnboarding().then(data => {
      if (current) setState({ context, ...data });
    }, () => { if (current) setState({ context, shows: [], error: true }); });
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => { current = false; window.removeEventListener('focus', onFocus); };
  }, [context, revision]);
  if (state.context !== context) return null;
  if (state.error) return <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => refresh()}>Retry</Button>}>Urgent client onboarding alerts could not be checked.</Alert>;
  if (!state.shows.length && !state.truncated) return null;
  return <Alert severity="error" sx={{ mb: 2, border: '1px solid #FD355A', borderRadius: 2, bgcolor: 'rgba(253,53,90,.12)', color: '#fff', '& .MuiAlert-message': { width: '100%', minWidth: 0 } }}>
    <AlertTitle>{state.shows.length} show{state.shows.length === 1 ? '' : 's'} with urgent client onboarding</AlertTitle>
    <Typography variant="body2" sx={{ mb: 1 }}>Incomplete client checklist for shows within seven days or already underway.</Typography>
    {state.shows.map(show => <Box key={show.eventId} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1 }}>
      <Typography variant="body2" sx={{ flex: '1 1 260px', overflowWrap: 'anywhere' }}><strong>{show.eventName}</strong> — {show.clientOnboarding.reasons.join('; ')}</Typography>
      <Button component={RouterLink} to={`/show?eventId=${encodeURIComponent(show.eventId)}`} variant="outlined" size="small" sx={{ color: '#fff', borderColor: '#D175B6', textTransform: 'none' }}>Open show</Button>
    </Box>)}
    {state.truncated && <Typography variant="body2" sx={{ mt: 1 }}>The show limit was reached. Review Verify → Clients for the full client queue.</Typography>}
  </Alert>;
}
